import { useState } from 'react';

import { api } from '../api';
import type { PortalConfig } from '../config';
import { AsyncBoundary, Notice, StatusBadge, formatDate, useAsync } from '../ui';

export function RepositoriesView({ config }: { config: PortalConfig }) {
  const state = useAsync(() => api.repositories(config), [config]);
  const [scanning, setScanning] = useState<string | undefined>();
  const [message, setMessage] = useState<string | undefined>();

  async function scan(projectId: string, name: string) {
    setScanning(projectId);
    setMessage(undefined);

    try {
      await api.startRepositoryScan(config, projectId);
      setMessage(`Scan started for ${name}. Findings appear once it finishes.`);
      state.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not start the scan.');
    } finally {
      setScanning(undefined);
    }
  }

  return (
    <>
      <p className="muted">
        Every repository the Amazon Inspector GitHub App can see is scanned automatically, including on each pull
        request. Nothing needs to be added to a repository for it to appear here.
      </p>

      {message ? <Notice>{message}</Notice> : null}

      <div className="section">
        <AsyncBoundary state={state}>
          {({ repositories }) =>
            repositories.length === 0 ? (
              <Notice kind="warn">
                No repositories yet. If the GitHub integration was only just connected, Inspector can take a few minutes
                to import the organisation.
              </Notice>
            ) : (
              <div className="card">
                <table>
                  <thead>
                    <tr>
                      <th>Repository</th>
                      <th>Visibility</th>
                      <th>Last scan</th>
                      <th>Commit</th>
                      <th>Triggers</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {repositories.map((repository) => (
                      <tr key={repository.projectId}>
                        <td>
                          <div className="finding-title">{repository.name}</div>
                          {repository.scanStatusReason && repository.scanStatus !== 'SUCCESSFUL' ? (
                            <div className="finding-desc">{repository.scanStatusReason}</div>
                          ) : null}
                        </td>
                        <td className="muted">{repository.visibility ?? '—'}</td>
                        <td>
                          {repository.scanStatus ? <StatusBadge value={repository.scanStatus} /> : '—'}
                          <div className="muted">{formatDate(repository.lastScannedAt)}</div>
                        </td>
                        <td>
                          <code>{repository.lastScannedCommitId?.slice(0, 8) ?? '—'}</code>
                        </td>
                        <td className="muted">
                          {[
                            repository.pullRequestScanning ? 'pull request' : undefined,
                            repository.periodicScanning ? 'scheduled' : undefined,
                          ]
                            .filter(Boolean)
                            .join(', ') || 'none'}
                        </td>
                        <td>
                          <button
                            className="action secondary"
                            disabled={scanning === repository.projectId}
                            onClick={() => scan(repository.projectId, repository.name)}
                          >
                            {scanning === repository.projectId ? 'Starting…' : 'Scan now'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </AsyncBoundary>
      </div>
    </>
  );
}
